import type {
  ColumnDef,
  PaginationState,
  OnChangeFn,
  RowSelectionState,
  SortingState,
} from "@tanstack/react-table";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getExpandedRowModel,
  getSortedRowModel,
} from "@tanstack/react-table";
import {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
  useLayoutEffect,
  Fragment,
  UIEvent,
} from "react";
import classNames from "classnames";
import Checkbox from "../Checkbox/Checkbox";
import Button from "../Button/Button";
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, Help } from "../Icon/Icon";
import TablePaginationPages from "./TablePaginationPages";
import styles from "./Table.module.scss";
import NoResults from "./NoResults";
import Tooltip from "../Tooltip/Tooltip";
import Loader from "../Loader/Loader";

export interface SelectedToolbarProps {
  ids: string[];
  isAllSelected: boolean;
  totalCount?: number;
}

interface TableProps<T extends object> {
  columns: {
    [K in keyof T]: ColumnDef<T, T[K]>;
  }[keyof T][];
  data?: T[];
  isSelectable?: boolean;
  SelectedToolbar?: ({
    ids,
    isAllSelected,
  }: SelectedToolbarProps) => React.ReactElement;
  Footer?: React.ReactNode;
  Header?: React.ReactNode;
  totalCount?: number;
  pagination?: PaginationState;
  setPagination?: React.Dispatch<React.SetStateAction<PaginationState>>;
  className?: string;
  clearSelectionOnChange?: boolean;
  showSelectionHeader?: boolean;
  defaultRowSelection?: RowSelectionState;
  onRowSelectionChange?: (newRowSelection: RowSelectionState) => void;
  onSortingChange?: (newSorting: SortingState) => void;
  loading?: boolean;
  resourceName?: {
    singular: string;
    plural: string;
  };
  enableSorting?: boolean;
  isFiltered?: boolean;
  rowSelection?: RowSelectionState;
  setRowSelection?: React.Dispatch<React.SetStateAction<RowSelectionState>>;
  showBorder?: boolean;
  emptyState?: React.ReactNode;
  disableSelectAll?: boolean;
}

function Table<T extends object>({
  data = [],
  columns,
  isSelectable,
  SelectedToolbar,
  Footer,
  Header,
  totalCount,
  pagination,
  setPagination,
  className,
  clearSelectionOnChange = true,
  showSelectionHeader = true,
  defaultRowSelection = {},
  onRowSelectionChange,
  onSortingChange,
  loading = false,
  resourceName = {
    singular: "row",
    plural: "rows",
  },
  enableSorting = false,
  isFiltered = false,
  rowSelection: controlledRowSelection,
  setRowSelection: controlledSetRowSelection,
  showBorder,
  emptyState,
  disableSelectAll,
}: TableProps<T>) {
  const selectableColumn: ColumnDef<T> = useMemo(
    () => ({
      id: "select",
      size: 49,
      header: ({ table }) => (
        <Checkbox
          {...{
            disabled: table.getRowModel().rows.length === 0,
            checked: table.getIsAllPageRowsSelected(),
            indeterminate: table.getIsSomePageRowsSelected(),
            onChange: table.getToggleAllPageRowsSelectedHandler(),
          }}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          {...{
            checked: row.getIsSelected(),
            disabled: !row.getCanSelect(),
            indeterminate: row.getIsSomeSelected(),
            onChange: row.getToggleSelectedHandler(),
          }}
        />
      ),
    }),
    [],
  );

  const [internalRowSelection, setInternalRowSelection] =
    useState<RowSelectionState>(defaultRowSelection);
  const rowSelection = controlledRowSelection ?? internalRowSelection;
  const setRowSelection = controlledSetRowSelection ?? setInternalRowSelection;

  const [sorting, setSorting] = useState<SortingState>([]);
  const [allSelected, setAllSelected] = useState(false);
  const rowSelectionRef = useRef<RowSelectionState>(defaultRowSelection);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const viewableTableRef = useRef<HTMLDivElement>(null);
  const reactTableRef = useRef<ReturnType<typeof useReactTable<T>>>(null);
  const pageCount =
    totalCount && pagination ? Math.ceil(totalCount / pagination.pageSize) : -1;
  const [overflowState, setOverflowState] = useState({
    top: false,
    bottom: false,
    left: false,
    right: false,
  });
  const firstRowRef = useRef<HTMLTableRowElement>(null);
  const lastRowRef = useRef<HTMLTableRowElement>(null);

  const internalColumns = useMemo(() => {
    return [...(isSelectable ? [selectableColumn] : []), ...columns];
  }, [columns, isSelectable, selectableColumn]);

  useEffect(() => {
    rowSelectionRef.current = rowSelection;
  }, [rowSelection]);

  useEffect(() => {
    onRowSelectionChange?.(rowSelectionRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPaginationChange = useCallback<OnChangeFn<PaginationState>>(
    (newPagination) => {
      setPagination?.(newPagination);
      tableWrapperRef.current?.scrollTo(0, 0);

      if (clearSelectionOnChange) {
        setAllSelected(false);
        reactTableRef.current?.resetRowSelection();
      }
    },
    [clearSelectionOnChange, setPagination],
  );

  const onSortingChangeInternal = useCallback<OnChangeFn<SortingState>>(
    (newSorting) => {
      const newSortingState =
        typeof newSorting === "function"
          ? newSorting(sorting)
          : (newSorting ?? []);
      setSorting(newSortingState);
      onSortingChange?.(newSortingState);
      reactTableRef.current?.resetRowSelection();
      reactTableRef.current?.resetPageIndex();
      tableWrapperRef.current?.scrollTo(0, 0);
    },
    [onSortingChange, sorting],
  );

  const onRowSelectionChangeInternal = useCallback<
    OnChangeFn<RowSelectionState>
  >(
    (newRowSelection) => {
      const newSelection =
        typeof newRowSelection === "function"
          ? newRowSelection(rowSelectionRef.current)
          : newRowSelection;

      setRowSelection(newSelection);
      onRowSelectionChange?.(newSelection);
      setAllSelected(false);
    },
    [onRowSelectionChange, setRowSelection],
  );

  const table = useReactTable({
    data,
    columns: internalColumns,
    defaultColumn: {
      size: 0,
      minSize: 0,
      maxSize: 0,
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    pageCount,
    state: {
      rowSelection,
      pagination,
      sorting,
    },
    meta: {
      tableWrapperRef,
    },
    onPaginationChange,
    enableSorting,
    onSortingChange: onSortingChangeInternal,
    manualSorting: true,
    manualPagination: true,
    enableRowSelection: Boolean(isSelectable),
    onRowSelectionChange: onRowSelectionChangeInternal,
    getRowId: (row, index) =>
      "id" in row && typeof row.id === "string" ? row.id : index.toString(),
  });

  useEffect(() => {
    reactTableRef.current = table;
  }, [table]);

  useEffect(() => {
    if (clearSelectionOnChange) {
      onRowSelectionChangeInternal({});
    }
  }, [clearSelectionOnChange, data, onRowSelectionChangeInternal]);

  const setLeftRightOverflowState = useCallback(
    ({
      scrollWidth,
      scrollLeft,
      clientWidth,
    }: {
      scrollWidth: number;
      scrollLeft: number;
      clientWidth: number;
    }) => {
      setOverflowState((prevState) => ({
        ...prevState,
        left: scrollLeft > 0,
        right: scrollLeft + clientWidth < scrollWidth,
      }));
    },
    [],
  );

  const onScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;

      setLeftRightOverflowState(target);
    },
    [setLeftRightOverflowState],
  );

  useLayoutEffect(() => {
    if (tableWrapperRef.current) {
      setLeftRightOverflowState(tableWrapperRef.current);
    }
  }, [columns, setLeftRightOverflowState]);

  useLayoutEffect(() => {
    if (tableWrapperRef.current) {
      setLeftRightOverflowState(tableWrapperRef.current);
    }

    const onResize = () => {
      if (tableWrapperRef.current) {
        setLeftRightOverflowState(tableWrapperRef?.current);
      }
    };

    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [setLeftRightOverflowState]);

  useLayoutEffect(() => {
    const firstRowObserver = new IntersectionObserver(
      ([entry]) => {
        setOverflowState((prevState) => ({
          ...prevState,
          top: !entry.isIntersecting,
        }));
      },
      { root: viewableTableRef.current },
    );
    const lastRowObserver = new IntersectionObserver(
      ([entry]) => {
        setOverflowState((prevState) => ({
          ...prevState,
          bottom: !entry.isIntersecting,
        }));
      },
      { root: viewableTableRef.current },
    );

    if (lastRowRef.current && firstRowRef.current) {
      firstRowObserver.observe(firstRowRef.current);
      lastRowObserver.observe(lastRowRef.current);
    } else {
      setOverflowState((prevState) => ({
        ...prevState,
        bottom: false,
        top: false,
      }));
    }

    return () => {
      firstRowObserver.disconnect();
      lastRowObserver.disconnect();
    };
  }, [data]);

  const isEmpty = !loading && table.getRowModel().rows.length === 0;

  return (
    <div
      className={classNames(styles.tableContainer, className, {
        [styles.tableContainerEmpty]: isEmpty,
        [styles.tableContainerNoPagination]: pageCount < 2 && !loading,
      })}
    >
      {Header ? <div className={styles.header}>{Header}</div> : null}
      <div ref={viewableTableRef} className={styles.tableScrollableWrapper}>
        {loading ? (
          <Loader className={styles.loader} withOverlay color="white" />
        ) : null}
        <div
          className={styles.tableScrollableOverlays}
          style={
            // Prevent overlay from being out of position when scrollbar is visible
            tableWrapperRef.current
              ? {
                  width: `calc(100% - ${
                    tableWrapperRef.current.offsetWidth -
                    tableWrapperRef.current.clientWidth
                  }px)`,
                  height: `calc(100% - ${
                    tableWrapperRef.current.offsetHeight -
                    tableWrapperRef.current.clientHeight
                  }px)`,
                }
              : undefined
          }
        >
          <div
            className={classNames(styles.tableScrollableOverlayTop, {
              [styles.scrollable]: overflowState.top,
            })}
          />
          <div
            className={classNames(styles.tableScrollableOverlayBottom, {
              [styles.scrollable]: overflowState.bottom,
            })}
          />
          <div
            className={classNames(styles.tableScrollableOverlayLeft, {
              [styles.scrollable]:
                overflowState.left &&
                !(
                  table.getAllColumns()[0]?.columnDef.meta?.sticky ||
                  table.getAllColumns()[1]?.columnDef.meta?.sticky
                ),
            })}
          />
          <div
            className={classNames(styles.tableScrollableOverlayRight, {
              [styles.scrollableWithSticky]: !!table.getAllColumns().at(-1)
                ?.columnDef.meta?.sticky,
              [styles.scrollable]: overflowState.right,
            })}
          />
        </div>
        <div
          className={styles.tableWrapper}
          ref={tableWrapperRef}
          onScroll={onScroll}
        >
          <table className={styles.table}>
            <thead className={styles.tableHead}>
              {table.getHeaderGroups().map((headerGroup) => (
                <Fragment key={headerGroup.id}>
                  <tr
                    style={{
                      visibility:
                        Object.keys(rowSelection).length === 0
                          ? undefined
                          : "collapse",
                    }}
                  >
                    {headerGroup.headers.map((header, index) => (
                      <th
                        className={classNames(styles.headerCell, {
                          [styles.tableCellBordered]: showBorder,
                          [styles.withSelection]: isSelectable,
                          [styles.selectionColumnHeader]:
                            header.id === "select",
                          [styles.horizontalStickyHeader]:
                            header.column.columnDef.meta?.sticky,
                          [styles.stickyBorder]:
                            header.column.columnDef.meta?.sticky &&
                            ((overflowState.left &&
                              index + 1 !== headerGroup.headers.length) ||
                              (overflowState.right &&
                                index + 1 === headerGroup.headers.length)),
                          [styles.actionHeader]: header.id === "actions",
                        })}
                        // We need to default size to 1 to prevent thead from collapsing
                        style={{
                          width: header.column.columnDef.size || undefined,
                          minWidth:
                            header.column.columnDef.minSize ||
                            header.column.columnDef.size ||
                            1,
                          maxWidth:
                            header.column.columnDef.maxSize ||
                            header.column.columnDef.size ||
                            1,
                        }}
                        key={header.id}
                      >
                        {header.isPlaceholder ? null : header.column.getCanSort() &&
                          table.getRowModel().rows.length > 0 ? (
                          <button
                            className={classNames(styles.headerCellContent, {
                              [styles.headerCellContentSortable]:
                                header.column.getCanSort(),
                            })}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                            {header.column.columnDef.meta?.tooltip && (
                              <Tooltip
                                asChild
                                content={header.column.columnDef.meta?.tooltip}
                              >
                                <span>
                                  <Help className={styles.helpIcon} />
                                </span>
                              </Tooltip>
                            )}
                            {
                              {
                                asc: <ArrowUp />,
                                desc: <ArrowDown />,
                              }[header.column.getIsSorted() as string]
                            }
                          </button>
                        ) : (
                          <div className={styles.headerCellContent}>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                            {header.column.columnDef.meta?.tooltip && (
                              <Tooltip
                                content={header.column.columnDef.meta?.tooltip}
                              >
                                <Button variant="unstyled">
                                  <Help className={styles.helpIcon} />
                                </Button>
                              </Tooltip>
                            )}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                  {Object.keys(rowSelection).length > 0 && (
                    <tr>
                      <th
                        className={classNames(
                          styles.headerCell,
                          styles.selectionColumnHeader,
                          {
                            [styles.tableCellBordered]: showBorder,
                            [styles.withSelection]: isSelectable,
                          },
                        )}
                      >
                        <div className={styles.headerCellContent}>
                          {flexRender(
                            headerGroup.headers[0].column.columnDef.header,
                            headerGroup.headers[0].getContext(),
                          )}
                        </div>
                      </th>
                      <th
                        className={classNames(
                          styles.headerCell,
                          styles.selectedHeader,
                          {
                            [styles.tableCellBordered]: showBorder,
                            [styles.withSelection]: isSelectable,
                          },
                        )}
                      >
                        <div className={styles.headerCellContent}>
                          <span>
                            {allSelected
                              ? totalCount
                              : Object.keys(rowSelection).length}{" "}
                            selected
                          </span>
                          <div>
                            {SelectedToolbar ? (
                              <SelectedToolbar
                                totalCount={totalCount}
                                ids={Object.keys(rowSelection)}
                                isAllSelected={allSelected}
                              />
                            ) : null}
                          </div>
                        </div>
                      </th>
                      <th
                        className={classNames(styles.headerCell, {
                          [styles.tableCellBordered]: showBorder,
                        })}
                        colSpan={headerGroup.headers.length - 2}
                      />
                    </tr>
                  )}

                  {showSelectionHeader && table.getIsAllPageRowsSelected() ? (
                    <tr>
                      <th
                        className={styles.selectAllHeader}
                        colSpan={headerGroup.headers.length}
                      >
                        <div>
                          {allSelected && typeof totalCount === "number" ? (
                            <span>
                              {`All ${new Intl.NumberFormat().format(
                                totalCount,
                              )} ${
                                resourceName.plural
                              } across all pages are selected.`}
                            </span>
                          ) : (
                            <>
                              <span>
                                {`All ${table.getRowModel().rows.length} ${
                                  resourceName.plural
                                } on this page are selected.`}
                              </span>
                              {!disableSelectAll &&
                                typeof totalCount === "number" &&
                                totalCount >
                                  Object.keys(rowSelection).length && (
                                  <Button
                                    className={styles.selectAllButton}
                                    onClick={() => {
                                      setRowSelection(
                                        table.getRowModel().rows.reduce(
                                          (selection, row) => ({
                                            ...selection,
                                            [row.id]: true,
                                          }),
                                          {},
                                        ),
                                      );
                                      setAllSelected(true);
                                    }}
                                  >
                                    {`Select all ${new Intl.NumberFormat().format(
                                      totalCount,
                                    )} ${resourceName.plural}`}
                                  </Button>
                                )}
                            </>
                          )}
                        </div>
                      </th>
                    </tr>
                  ) : null}
                </Fragment>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row, index) => (
                <tr
                  key={row.id}
                  ref={
                    index === 0
                      ? firstRowRef
                      : index === table.getRowModel().rows.length - 1
                        ? lastRowRef
                        : undefined
                  }
                >
                  {row.getVisibleCells().map((cell, index) => (
                    <td
                      style={{
                        width: cell.column.columnDef.size || undefined,
                        minWidth:
                          cell.column.columnDef.minSize ||
                          cell.column.columnDef.size,
                        maxWidth:
                          cell.column.columnDef.maxSize ||
                          cell.column.columnDef.size,
                      }}
                      className={classNames(styles.bodyCell, {
                        [styles.tableCellBordered]: showBorder,
                        [styles.actionCell]: cell.column.id === "actions",
                        [styles.selectionColumnCell]:
                          cell.column.id === "select",
                        [styles.stickyBorder]:
                          cell.column.columnDef.meta?.sticky &&
                          ((overflowState.left &&
                            index + 1 !== row.getVisibleCells().length) ||
                            (overflowState.right &&
                              index + 1 === row.getVisibleCells().length)),
                        [styles.horizontalStickyCell]:
                          cell.column.columnDef.meta?.sticky,
                        [styles.withSelection]: isSelectable,
                      })}
                      key={cell.id}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {!!pagination && !!pageCount && pageCount > 1 && (
        <div className={classNames(styles.footer, styles.pagination)}>
          <Button
            disabled={!table.getCanPreviousPage()}
            leftIcon={<ArrowLeft size="medium" />}
            onClick={() => {
              table.previousPage();
            }}
            variant="outline"
          >
            Previous
          </Button>
          <TablePaginationPages
            onSelectPage={table.setPageIndex}
            pageCount={pageCount}
            pageIndex={pagination.pageIndex}
          />
          <Button
            disabled={!table.getCanNextPage()}
            onClick={() => {
              table.nextPage();
            }}
            rightIcon={<ArrowRight size="medium" />}
            variant="outline"
          >
            Next
          </Button>
        </div>
      )}
      {isEmpty &&
        (isFiltered ? (
          <NoResults
            resourceName={resourceName.plural}
            isFiltered={isFiltered}
          />
        ) : (
          (emptyState ?? (
            <NoResults
              resourceName={resourceName.plural}
              isFiltered={isFiltered}
            />
          ))
        ))}
      {Footer && <div className={styles.footer}>{Footer}</div>}
    </div>
  );
}

export default Table;
